import { Box, Typography, Link, Chip } from "@mui/material";
import { Language, Mail, Shield } from "@mui/icons-material";
import type { AppConfig } from "../../interfaces/appIntegration.interfaces";
import FlownectLogo from "../../../../assets/images/flownectlogo.png";

interface Props {
  app: AppConfig;
}

export const AppDetailDescription = ({ app }: Props) => {
  const isInternal = app.developer.isInternal;
  const isInstalled = app.isInstalled;

  return (
    <Box
      sx={{
        mb: 8,
        display: { xs: "column", md: "grid" },
        gridTemplateColumns: "1fr 300px",
        gap: 4,
        ml: 4,
        mr: 4,
      }}
    >
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 3, fontSize: "1.1rem" }}>
          Overview
        </Typography>

        <Typography sx={{ color: "var(--fx-surface-textsecondary, #64748B)", lineHeight: 1.8, mb: 4, whiteSpace: "pre-line" }}>{app.longDescription}</Typography>
      </Box>

      <Box
        sx={{
          flexDirection: "column",
          gap: 2,

          p: 3,
          borderRadius: 1.5,
          border: { xs: "none", md: "1px solid var(--fx-surface-border, #E2E8F0)" },
          marginLeft: { xs: "-3.78rem", md: "auto" },
        }}
      >
        <Box sx={{ p: 3, bgcolor: "var(--fx-surface-base, #FFFFFF)", borderRadius: 1.5, border: "1px solid var(--fx-surface-border, #E2E8F0)", width: { xs: "280px" }, maxHeight: { xs: "100px" } }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2 }}>
            Categories
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip
              label={app.category}
              variant="outlined"
              size="small"
              sx={{
                textTransform: "capitalize",
                fontWeight: 700,
                borderColor: "#2563eb",
                color: "#2563eb",
                padding: { xs: 0.5, md: 1 },
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            p: 3,
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            borderRadius: 1.5,
            border: "1px solid var(--fx-surface-border, #E2E8F0)",

            width: { xs: "280px" },
            maxHeight: { xs: "100px" },
            marginLeft: { xs: "auto", md: "auto" },
            marginTop: { xs: 2, md: 0 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Box
              component="img"
              src={isInternal ? FlownectLogo : app.icon}
              alt={app.developer.name}
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                backgroundColor: "var(--fx-surface-muted, #F1F5F9)",
                p: 0.5,
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography variant="caption" sx={{ color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 700, display: "block", fontSize: "0.7rem" }}>
                {isInternal ? "Developed by" : "Third-party integration by"}
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: "0.95rem" }}>{app.developer.name}</Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            p: 3,
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            borderRadius: 1.5,
            border: "1px solid var(--fx-surface-border, #E2E8F0)",

            width: { xs: "280px" },
            maxHeight: { xs: "10rem" },
            marginLeft: { xs: "auto", md: "auto" },
            marginTop: { xs: 2, md: 0 },
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2 }}>
            Support & Resources
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 0, md: 2 } }}>
            <Link
              href={app.documentationUrl}
              target="_blank"
              rel="noopener"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                color: "#2563eb",

                textDecoration: "none",
                fontSize: "0.9rem",
                fontWeight: 600,

                "&:hover": { textDecoration: "underline" },
              }}
            >
              {!isInternal && (
                <>
                  <Language sx={{ fontSize: 18 }} />
                  Visit our Website
                </>
              )}
            </Link>
            <Link
              href={`mailto:${app.supportEmail}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                color: "#2563eb",
                textDecoration: "none",
                fontSize: "0.9rem",
                fontWeight: 600,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              <Mail sx={{ fontSize: 18 }} /> Contact us for support
            </Link>
            <Link
              href="#"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                color: "#2563eb",
                textDecoration: "none",
                fontSize: "0.9rem",
                fontWeight: 600,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              <Shield sx={{ fontSize: 18 }} /> Privacy Policy
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
